import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Session } from '../../api/session';
import { getBanners, getNewMessagesCount, getObject, getObjectAddresses, getPasses, getTemplates, type ObjectInfo } from '../../api/tenant';
import { passOptions, type GuestKind } from '../../domain/availability';
import { homeOrderRoute } from '../../domain/homeOrder';
import { PassStatus, frequentGuests, todayPasses, type FrequentGuest } from '../../domain/passes';
import { isFullPlate, normalizePlate } from '../../domain/plate';
import type { TelegramApi } from '../../telegram/webapp';
import { BannerSlot } from '../../ui/BannerSlot';
import { GostPlateInput } from '../../ui/GostPlate';
import { Plate, WalkerIcon } from '../../ui/Plate';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../common/LoadFailed';
import { PassRow } from '../common/PassRow';
import { Onboarding } from '../onboarding/Onboarding';
import { Announcements } from './Announcements';
import { OrderDone } from './OrderDone';
import { useHomeOrder, type HomeGuest } from './useHomeOrder';
import h from './home.module.css';
import s from './homeB.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
  objects: ObjectInfo[];
  onSwitchObject: (id: number) => void;
}

const SAVED_SHOWN = 3;
const PURPOSES_SHOWN = 4;
/**
 * The page folds to the entry while typing only where an on-screen keyboard takes half the screen: on a touch
 * screen. In a browser on a computer the banner, «Шаблоны» and «Сегодня» stay (#4216).
 */
const SOFT_KEYBOARD = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

const guestOf = (g: FrequentGuest): HomeGuest => (g.plate ? { kind: 'vehicle', plate: g.plate } : { kind: 'pedestrian', name: g.label });

/**
 * Home screen B («Номер на главной», prototype 2026-10-29): 75% of passes are one-time car passes, so the plate is
 * typed right here and «Заказать пропуск» orders it for today. Saved guests are one tap plus a confirming sheet.
 */
export function HomeScreenB({ session, telegram, object, objects, onSwitchObject }: Props) {
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [kind, setKind] = useState<GuestKind | null>(null);
  const [plate, setPlate] = useState('');
  const [region, setRegion] = useState('');
  const [name, setName] = useState('');
  const [purposeId, setPurposeId] = useState<number | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [warned, setWarned] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [plateField, setPlateField] = useState<'plate' | 'region' | null>(null);
  const [sheet, setSheet] = useState<FrequentGuest | null>(null);

  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const unread = useQuery({ queryKey: ['new-count'], queryFn: () => getNewMessagesCount(session) });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => getTemplates(session) });
  const ids = useMemo(() => (addresses.data ?? []).map((a) => a.id), [addresses.data]);
  const recent = useQuery({ queryKey: ['passes', 'recent', object.id], queryFn: () => getPasses(session, { addressIds: ids }), enabled: ids.length > 0 });
  const active = useQuery({
    queryKey: ['passes', 'active', object.id],
    queryFn: () => getPasses(session, { addressIds: ids, statuses: [PassStatus.Inside, PassStatus.Expected, PassStatus.WaitingGuest] }),
    enabled: ids.length > 0,
  });
  const banners = useQuery({ queryKey: ['banners', object.id], queryFn: () => getBanners(session, object.id) });

  const opts = useMemo(() => passOptions(details.data?.settings ?? {}), [details.data]);
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);
  const address = usable[0] ?? null;
  const guest: GuestKind | null = kind ?? opts.guestTypes[0] ?? null;
  useEffect(() => setKind(null), [object.id]);

  const saved = useMemo(() => {
    const own = new Set(ids);
    return frequentGuests((templates.data ?? []).filter((t) => own.has(t.address.id)), recent.data ?? []);
  }, [ids, templates.data, recent.data]);
  // «Шаблоны» opens a template here as /?order=template:<id>: the same order sheet as a tap on the home screen.
  const [params, setParams] = useSearchParams();
  const orderKey = params.get('order');
  useEffect(() => {
    if (!orderKey || !templates.data || !ids.length) return;
    const g = saved.find((x) => x.key === orderKey);
    if (g) setSheet(g);
    setParams((p) => { p.delete('order'); return p; }, { replace: true });
  }, [orderKey, templates.data, ids.length, saved, setParams]);
  const today = useMemo(() => todayPasses(active.data ?? [], new Date()).filter((p) => p.durationType !== 3), [active.data]);
  const fullPlate = normalizePlate(plate + region);
  const suggestions = guest === 'vehicle' && plate.length > 0 ? saved.filter((g) => g.plate?.startsWith(normalizePlate(plate))).slice(0, 3) : [];

  const order = useHomeOrder({
    session,
    telegram,
    details: details.data ?? { ...object, settings: {}, options: [], vehicleTypes: [] },
    address: address ?? { id: 0, name: '' },
  });

  if (addresses.isError || details.isError) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <div className={s.page} aria-busy="true">
        <Skeleton height={52} />
        <Skeleton height={84} />
        <Skeleton height={56} />
      </div>
    );
  }

  if (order.done) {
    return (
      <OrderDone
        done={order.done}
        session={session}
        telegram={telegram}
        object={object}
        banners={banners.data ?? []}
        onClose={() => {
          // The input was focused when the order went out and left the page without a blur.
          setTyping(false);
          order.reset();
          setPlate('');
          setRegion('');
          setName('');
          setPurposeId(null);
          setWarned(null);
        }}
      />
    );
  }

  const planFor = (g: GuestKind) =>
    homeOrderRoute({
      opts,
      guest: g,
      requireModel: details.data.settings.passesRequireModel === true,
      vehicleTypes: details.data.vehicleTypes.length,
      usableAddresses: usable.length,
    });

  const submit = (who: HomeGuest, key: string) => {
    const route = planFor(who.kind);
    if (route.kind === 'unavailable') return setHint('Такой пропуск на объекте недоступен');
    if (route.kind === 'form') return void navigate(`/new?guest=${encodeURIComponent(key)}`);
    order.order(who, purposeId, route);
  };

  const onOrder = () => {
    if (!guest) return;
    setHint(null);
    if (guest === 'vehicle') {
      if (fullPlate.length < 3) return setHint('Впишите номер гостя, например А 123 ВС 77');
      // No form between the tap and the pass: a plate cut short gets one warning before it is ordered.
      if (!isFullPlate(fullPlate) && warned !== fullPlate) {
        setWarned(fullPlate);
        return setHint('Проверьте номер — похоже, он неполный. Нажмите ещё раз, чтобы заказать так');
      }
      return submit({ kind: 'vehicle', plate: fullPlate }, `vehicle:${fullPlate}`);
    }
    if (name.trim().length < 2) return setHint('Впишите имя гостя');
    submit({ kind: 'pedestrian', name: name.trim() }, `pedestrian:${name.trim()}`);
  };

  const noOrders = !opts.guestTypes.length || !address;
  const message = hint ?? order.error;

  return (
    <div className={typing && SOFT_KEYBOARD ? `${s.page} ${s.typing}` : s.page}>
      <header className={`${h.header} ${s.collapse}`}>
        <button
          type="button"
          className={h.objectButton}
          onClick={() => objects.length > 1 && setSwitcherOpen((v) => !v)}
          aria-expanded={objects.length > 1 ? switcherOpen : undefined}
          aria-haspopup={objects.length > 1 ? 'listbox' : undefined}
        >
          <span className={h.objectName}>
            {object.name}
            {objects.length > 1 && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            )}
          </span>
          <span className={h.address}>{usable.length === 1 ? usable[0].name : usable.length ? `Адресов: ${usable.length}` : ''}</span>
        </button>
        <Link to="/notifications" className={h.bell} aria-label={unread.data ? `Уведомления: ${unread.data} новых` : 'Уведомления'}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 16v-5a6 6 0 1 1 12 0v5l1.5 2h-15z" />
            <path d="M10 20.5a2 2 0 0 0 4 0" />
          </svg>
          {!!unread.data && <span className={h.badge}>{unread.data}</span>}
        </Link>
      </header>
      {switcherOpen && (
        <div role="listbox" aria-label="Объекты" className={h.switcher}>
          {objects.map((o) => (
            <button
              key={o.id}
              type="button"
              role="option"
              aria-selected={o.id === object.id}
              className={o.id === object.id ? `${h.option} ${h.optionCurrent}` : h.option}
              onClick={() => {
                setSwitcherOpen(false);
                if (o.id !== object.id) onSwitchObject(o.id);
              }}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
      <div className={s.collapse}>
        <Announcements session={session} unread={unread.data ?? 0} compact />
      </div>
      <h1 className={`${h.title} ${s.title} ${s.collapse}`}>Кого ждём?</h1>

      {noOrders ? (
        <section className={s.closed} aria-label="Заказ недоступен">
          <h2>{address ? 'Пропуска здесь оформляет управляющая компания' : 'Заказ недоступен'}</h2>
          <p>{address ? 'На этом объекте жители не заказывают пропуска сами. Напишите в УК — она оформит пропуск.' : 'Адрес заблокирован управляющей компанией. Позвоните в УК, чтобы узнать причину.'}</p>
          <Link to="/requests/new" className={h.primary}>
            Написать в УК
          </Link>
        </section>
      ) : (
        <>
          {opts.guestTypes.length > 1 && (
            <div className={s.kind} role="radiogroup" aria-label="Кто приедет">
              {(['vehicle', 'pedestrian'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={guest === g}
                  onMouseDown={(e) => e.preventDefault()}
                  // The focused input is replaced by the other kind's one and never sends a blur.
                  onClick={() => { setKind(g); setHint(null); setTyping(false); }}
                >
                  {g === 'vehicle' ? 'Автомобиль' : 'Пешеход'}
                </button>
              ))}
            </div>
          )}

          <div className={s.entry}>
            {guest === 'vehicle' ? (
              <>
                <div className={s.plateWrap}>
                  <GostPlateInput
                    plate={plate}
                    region={region}
                    focused={plateField}
                    note="можно латиницей"
                    onFocus={(f) => { setTyping(true); setPlateField(f); }}
                    onBlur={() => { setTyping(false); setPlateField(null); }}
                    // The input masks itself to the GOST template: the values here are already clean.
                    onPlate={(v) => { setPlate(v); setHint(null); }}
                    onRegion={(v) => { setRegion(v); setHint(null); }}
                  />
                </div>
              </>
            ) : (
              <>
                <input
                  className={s.name}
                  aria-label="Имя гостя"
                  placeholder="Имя гостя, фамилия — по желанию"
                  value={name}
                  maxLength={255}
                  autoComplete="off"
                  onFocus={() => setTyping(true)}
                  onBlur={() => setTyping(false)}
                  onChange={(e) => { setName(e.target.value); setHint(null); }}
                />
              </>
            )}
          </div>

          {suggestions.length > 0 && (
            <div className={s.suggest} role="listbox" aria-label="Подсказки из истории">
              {suggestions.map((g) => (
                <button key={g.key} type="button" role="option" aria-selected="false" onMouseDown={(e) => e.preventDefault()} onClick={() => setSheet(g)}>
                  <Plate value={g.plate!} />
                  {g.label}
                </button>
              ))}
            </div>
          )}

          {details.data.options.length > 0 && (
            <div className={s.purposes} role="group" aria-label="Цель визита, необязательно">
              {details.data.options.slice(0, PURPOSES_SHOWN).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={s.purpose}
                  aria-pressed={purposeId === o.id}
                  // Picked while typing: keep the focus, or the header expands and the chip slips from under the finger.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setPurposeId(purposeId === o.id ? null : o.id)}
                >
                  {o.name}
                </button>
              ))}
            </div>
          )}

          {order.forceRequest && <p className={s.request}>На этом объекте такой пропуск согласует УК: отправим заявку, статус придёт в «Обращения».</p>}
          <button type="button" className={s.order} onMouseDown={(e) => e.preventDefault()} onClick={onOrder} disabled={order.pending}>
            {order.pending ? 'Оформляем…' : order.forceRequest ? 'Отправить на согласование' : 'Заказать пропуск'}
          </button>
          {message && (
            <p className={s.error} role="alert">
              {message}
            </p>
          )}
        </>
      )}

      <div className={s.collapse}>
        <BannerSlot banners={banners.data ?? []} telegram={telegram} placement="home" />
      </div>

      {/* Under the banner, so the partners' slot stays in the first screen of a Telegram Desktop window (≈600 px). */}
      {!noOrders && (
        // While typing the header above is collapsed: a blur on press would expand it and move the link from under the finger.
        <div className={s.minor}>
          <Link to={guest === 'vehicle' && fullPlate.length >= 3 ? `/new?guest=${encodeURIComponent(`vehicle:${fullPlate}`)}` : '/new'} onMouseDown={(e) => e.preventDefault()}>
            Параметры
          </Link>
          <Link to="/invite" onMouseDown={(e) => e.preventDefault()}>
            Приглашение
          </Link>
        </div>
      )}

      {!noOrders && saved.length > 0 && (
        <section className={`${s.saved} ${s.collapse}`} aria-label="Шаблоны">
          <div className={s.savedHead}>
            <span>Шаблоны</span>
            <Link to="/more/guests">Все</Link>
          </div>
          <div className={s.savedList}>
            {saved.slice(0, SAVED_SHOWN).map((g) => (
              <button key={g.key} type="button" className={s.savedItem} onClick={() => setSheet(g)}>
                {g.plate ? <Plate value={g.plate} /> : <WalkerIcon />}
                {g.label}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className={`${h.section} ${s.collapse}`}>
        <div className={s.savedHead} style={{ color: 'var(--c-text-2)', marginBottom: 0 }}>
          <h2 className={h.sectionTitle}>Сегодня</h2>
          <Link to="/passes" style={{ color: 'var(--c-accent-ink)' }}>Все пропуска</Link>
        </div>
        {active.isPending && ids.length ? (
          <Skeleton height={128} />
        ) : active.isError ? (
          <LoadFailed error={active.error} onRetry={() => void active.refetch()} />
        ) : today.length === 0 ? (
          <p className={h.hint}>На сегодня гостей нет</p>
        ) : (
          <ul className={h.today} aria-label="Сегодня">
            {today.map((p) => (
              <li key={p.id}>
                <PassRow pass={p} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {sheet && (
        <div className={s.scrim} onClick={(e) => e.target === e.currentTarget && setSheet(null)}>
          <section className={s.sheet} role="dialog" aria-modal="true" aria-labelledby="guest-sheet-title">
            <div className={s.grab} />
            <h2 id="guest-sheet-title">{sheet.label ? `Пропуск для гостя «${sheet.label}»` : 'Пропуск по шаблону'}</h2>
            <div className={s.receipt} style={{ marginBottom: 14 }}>
              <div className={s.row}>
                <span>Гость</span>
                <span>{sheet.plate ? <Plate value={sheet.plate} /> : 'пешком'}</span>
              </div>
              <div className={s.row}>
                <span>Когда</span>
                <span>сегодня, до 23:59</span>
              </div>
              <div className={s.row}>
                <span>Адрес</span>
                <span>{address?.name}</span>
              </div>
            </div>
            <button
              type="button"
              className={s.order}
              disabled={order.pending}
              onClick={() => {
                const g = sheet;
                setSheet(null);
                submit(guestOf(g), g.key);
              }}
            >
              Заказать пропуск
            </button>
            <button type="button" className={s.link} onClick={() => navigate(`/new?guest=${encodeURIComponent(sheet.key)}`)}>
              Изменить срок или цель
            </button>
          </section>
        </div>
      )}
      {/* «Ещё» → «Как пользоваться» opens /?intro=1: replay the intro here, as the old home screen did (#4216). */}
      <Onboarding
        telegram={telegram}
        replay={params.get('intro') === '1'}
        onClose={() => {
          if (!params.has('intro')) return;
          setParams((p) => { p.delete('intro'); return p; }, { replace: true });
        }}
      />
    </div>
  );
}
